import { WeatherValidationData, WEATHER_CONDITIONS } from '../types/validation';
import { WeatherData } from '../types';

const STORAGE_KEY_LAST_VALIDATION = 'weather_last_validation';
const STORAGE_KEY_USER_ID = 'weather_anon_user_id';
const COOLDOWN_HOURS = 12; // Ask max once every 12 hours per location

// Helper to get or create anonymous user ID
const getUserId = (): string => {
  let uid = localStorage.getItem(STORAGE_KEY_USER_ID);
  if (!uid) {
    uid = 'user_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem(STORAGE_KEY_USER_ID, uid);
  }
  return uid;
};

// Check if we should ask the user
export const shouldTriggerValidation = (
  weather: WeatherData,
  location: string
): boolean => {
  if (!weather || !location) return false;

  // 1. Check Rate Limit
  const lastValidationStr = localStorage.getItem(STORAGE_KEY_LAST_VALIDATION);
  if (lastValidationStr) {
    const lastValidation = JSON.parse(lastValidationStr);
    const timeDiff = Date.now() - lastValidation.timestamp;
    
    // If same location, check cooldown
    if (lastValidation.location === location && timeDiff < COOLDOWN_HOURS * 60 * 60 * 1000) {
      return false;
    }
  }

  // 2. Check Weather Condition (Trigger only on clear Rain or Clear/Cloudy signals)
  const conditionLower = weather.condition.toLowerCase();
  const rainChance = parseInt(weather.rainChance.replace('%', '')) || 0;

  const isRainy = conditionLower.includes('hujan') || rainChance > 60;
  const isClear = conditionLower.includes('cerah') || conditionLower.includes('berawan') || rainChance < 20;

  return isRainy || isClear;
};

// Map API condition to simple state
export const normalizeCondition = (weather: WeatherData): string => {
  const conditionLower = weather.condition.toLowerCase();
  const rainChance = parseInt(weather.rainChance.replace('%', '')) || 0;

  if (conditionLower.includes('hujan') || rainChance > 60) {
    return WEATHER_CONDITIONS.RAIN;
  }
  return WEATHER_CONDITIONS.NO_RAIN;
};

// Submit data (Mocking an API call / Discord Webhook)
export const submitWeatherValidation = async (
  location: string,
  weather: WeatherData,
  actualIsRaining: boolean
): Promise<boolean> => {
  try {
    const payload: WeatherValidationData = {
      id: Math.random().toString(36).substr(2, 9),
      userId: getUserId(),
      timestamp: Date.now(),
      location,
      predictedCondition: normalizeCondition(weather),
      actualCondition: actualIsRaining ? WEATHER_CONDITIONS.RAIN : WEATHER_CONDITIONS.NO_RAIN,
      rawForecast: weather.condition
    };

    console.log('[WeatherValidation] Submitting data:', payload);

    // Save to local storage to simulate "dataset"
    const dataset = JSON.parse(localStorage.getItem('weather_validation_dataset') || '[]');
    dataset.push(payload);
    localStorage.setItem('weather_validation_dataset', JSON.stringify(dataset));

    // Update last validation timestamp
    localStorage.setItem(STORAGE_KEY_LAST_VALIDATION, JSON.stringify({
      timestamp: Date.now(),
      location
    }));

    // In a real app, you would POST to an API or Discord Webhook here
    // await fetch('https://discord.com/api/webhooks/...', { ... })

    return true;
  } catch (error) {
    console.error('Failed to submit validation', error);
    return false;
  }
};
